import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Home, Link as LinkIcon, Mail, MapPin, MessageSquare, MoreVertical, Pencil, Phone, Plus, Trash2 } from 'lucide-react';
import WizardShell from '../ui/wizard-shell';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { WizardCard, WizardKVGrid, WizardKVItem, WizardSection } from '../ui/wizard-layout';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { kurseApi, masterdataApi, type KursItem, type UnterrichtsformItem } from '../../lib/courses';
import { getRoom, type Room } from '../../lib/rooms';
import { api } from '../../lib/api';
import { communicationsApi } from '../../lib/api/communications';
import { formatDateTime } from '../../lib/dateFormat';
import { getSubjectLabels } from '@/lib/settings';

export type StructuredParticipant = {
  subprofileId: string;
  wpUserId: number;
  subIndex: number;
  name: string;
};

export type CourseDetailsEvent = {
  /** Unterrichtseinheit-ID (stringified) */
  unitId?: number;
  /** Kurs-ID falls bekannt */
  courseId?: number | null;
  /** Raum-ID (aus Unit) falls bekannt */
  roomId?: number | null;
  /** Wochentag (1..7, Montag=1) */
  dayOfWeek?: number;

  name?: string;
  fachName?: string | null;
  startDate?: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endTime: string;   // HH:MM

  raumName?: string | null;
  standortAdresse?: string | null;
  /** Lehrkraft des Termins (Name) */
  lehrerName?: string | null;
  /** Lehrkraft des Termins (Personen-/Portal-ID) — für Anruf, SMS, E-Mail (T-0295). */
  lehrerId?: number | null;

  teilnehmer?: string | null;
  participants?: StructuredParticipant[] | null;

  notizen?: string | null;
};

/** Wer aus den Kursdetails angeschrieben wird. */
export type CourseContact = {
  wpUserId: number;
  subIndex: number | null;
  name: string;
};

type OnlineCourseSettings = {
  enabled: boolean;
  defaultMeetingUrl: string;
};

type Kontakt = { email: string | null; phone: string | null };

type KursEreignis = {
  id: number;
  type: string;
  description: string;
  actorName?: string | null;
  createdAt?: string | null;
};

export interface CourseDetailsWizardProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  event: CourseDetailsEvent | null;
  /** Für die Kontaktwege von Teilnehmern und Lehrkraft. */
  standortId?: number;

  onAddTrial?: (ev: CourseDetailsEvent) => void;

  // Actions (reuse existing ScheduleManager logic)
  onRescheduleCourse?: (ev: CourseDetailsEvent) => void;
  onCancelCourse?: (ev: CourseDetailsEvent) => void;
  onContactParticipants?: (ev: CourseDetailsEvent) => void;
  /** Eine Person anschreiben — über den Nachrichten-Assistenten, damit es in der Akte steht. */
  onContactPerson?: (kontakt: CourseContact, kanal: 'email' | 'sms') => void;
  onJumpToPerson?: (p: StructuredParticipant) => void;
  onJumpToTeacher?: (lehrerId: number) => void;
}

const WOCHENTAGE = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function safeUrl(u?: string | null): string {
  const t = (u ?? '').trim();
  if (!t) return '';
  // allow http(s) only to avoid weird schemes
  if (/^https?:\/\//i.test(t)) return t;
  return '';
}

function kurzeZeit(t?: string | null): string {
  return String(t || '').slice(0, 5);
}

function wochentagVon(ev: CourseDetailsEvent): string {
  if (ev.dayOfWeek && ev.dayOfWeek >= 1 && ev.dayOfWeek <= 7) return WOCHENTAGE[ev.dayOfWeek];
  if (ev.startDate) {
    const js = new Date(`${ev.startDate}T12:00:00`).getDay();
    return WOCHENTAGE[js === 0 ? 7 : js];
  }
  return '';
}

/** Anruf, SMS und E-Mail für eine Person — fehlt ein Kontaktweg, ist der Knopf aus. */
function KontaktKnoepfe({
  name,
  kontakt,
  onNachricht,
}: Readonly<{ name: string; kontakt?: Kontakt; onNachricht?: (kanal: 'email' | 'sms') => void }>) {
  const telefon = kontakt?.phone?.trim() || '';
  return (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        aria-label={`${name} anrufen`}
        title={telefon ? `Anrufen: ${telefon}` : 'Keine Telefonnummer'}
        disabled={!telefon}
        onClick={() => {
          globalThis.location.href = `tel:${telefon.replace(/[^\d+]/g, '')}`;
        }}
      >
        <Phone className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        aria-label={`SMS an ${name}`}
        title={telefon ? 'SMS schreiben' : 'Keine Telefonnummer'}
        disabled={!telefon || !onNachricht}
        onClick={() => onNachricht?.('sms')}
      >
        <MessageSquare className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        aria-label={`E-Mail an ${name}`}
        title={kontakt?.email ? `E-Mail: ${kontakt.email}` : 'Keine E-Mail-Adresse'}
        disabled={!kontakt?.email || !onNachricht}
        onClick={() => onNachricht?.('email')}
      >
        <Mail className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * Kursdetails (T-0295): Status und Belegung im Kopf, darunter Teilnehmer,
 * Lehrkraft, Kursdaten, Online-Unterricht und Notizen — und im zweiten Tab die
 * Ereignisse des Kurses in zeitlicher Reihenfolge.
 */
export default function CourseDetailsWizard({
  open,
  onOpenChange,
  event,
  standortId,
  onAddTrial,
  onRescheduleCourse,
  onCancelCourse,
  onContactParticipants,
  onContactPerson,
  onJumpToPerson,
  onJumpToTeacher,
}: CourseDetailsWizardProps) {
  const [course, setCourse] = useState<(KursItem & { status?: string | null }) | null>(null);
  const [form, setForm] = useState<UnterrichtsformItem | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(false);
  const [roomDetailsOpen, setRoomDetailsOpen] = useState(false);
  const [kontakte, setKontakte] = useState<Record<string, Kontakt>>({});
  const [ereignisse, setEreignisse] = useState<KursEreignis[]>([]);

  const [notizNeu, setNotizNeu] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // Die Notiz an diesem einen Termin (Unterrichtseinheit) — neben den Kursnotizen.
  const [terminNotiz, setTerminNotiz] = useState('');
  const [terminNotizEdit, setTerminNotizEdit] = useState(false);

  const [meetingDraft, setMeetingDraft] = useState('');
  const [meetingEdit, setMeetingEdit] = useState(false);
  const [savingMeeting, setSavingMeeting] = useState(false);
  const [onlineCfg, setOnlineCfg] = useState<OnlineCourseSettings | null>(null);

  const participants: StructuredParticipant[] = useMemo(() => {
    if (event?.participants && Array.isArray(event.participants)) return event.participants;
    return [];
  }, [event?.participants]);

  useEffect(() => {
    if (!open || !event) return;
    setSavingNote(false);
    setSavingMeeting(false);
    setMeetingEdit(false);
    setCourse(null);
    setForm(null);
    setRoom(null);
    setRoomDetailsOpen(false);
    setOnlineCfg(null);
    setMeetingDraft('');
    setNotizNeu('');
    setTerminNotiz(event.notizen ?? '');
    setTerminNotizEdit(false);
    setKontakte({});
    setEreignisse([]);
  }, [open, event]);

  const ladeEreignisse = async (courseId: number) => {
    const rows = await api
      .get<KursEreignis[]>(`/api/history?entity_type=course&entity_id=${courseId}&limit=500`)
      .catch(() => [] as KursEreignis[]);
    setEreignisse(Array.isArray(rows) ? rows : []);
  };

  useEffect(() => {
    const load = async () => {
      if (!open || !event) return;
      setLoading(true);
      try {
        const [cfg, loadedCourse, formen] = await Promise.all([
          api.get<OnlineCourseSettings>('/api/settings/online-course').catch(() => null),
          event.courseId ? kurseApi.get(event.courseId) : Promise.resolve(null),
          masterdataApi.unterrichtsformen().catch(() => [] as UnterrichtsformItem[]),
        ]);
        setOnlineCfg(cfg);
        setCourse(loadedCourse);
        setForm(formen.find((f) => f.id === loadedCourse?.unterrichtsform_id) ?? null);
        setMeetingDraft(safeUrl(loadedCourse?.online_meeting_url) || '');

        const roomId = (event.roomId ?? null) ?? (loadedCourse?.default_raum_id ?? null);
        if (roomId) {
          const r = await getRoom(roomId);
          setRoom(r);
        }
        if (event.courseId) await ladeEreignisse(event.courseId);
      } catch (e: any) {
        toast.error(e?.message || 'Kursdetails konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.courseId, event?.roomId]);

  // Telefon und E-Mail für die Kontaktknöpfe — dieselbe Auflösung wie im Versand,
  // gesperrte Kontaktwege bleiben damit aus.
  useEffect(() => {
    if (!open || !event || !standortId) return;
    const empfaenger = [
      ...participants.map((p) => ({ type: 'person' as const, wpUserId: p.wpUserId, subIndex: p.subIndex, clientId: `p:${p.subprofileId}` })),
      ...(event.lehrerId ? [{ type: 'person' as const, wpUserId: event.lehrerId, subIndex: null, clientId: 'lehrkraft' }] : []),
    ];
    if (empfaenger.length === 0) return;
    let aktiv = true;
    communicationsApi
      .resolveRecipients({ standortId, recipients: empfaenger })
      .then((res) => {
        if (!aktiv) return;
        const neu: Record<string, Kontakt> = {};
        for (const it of res.items || []) {
          const id = String(it.id || it.clientId || '');
          const gesperrt = Array.isArray(it.blockedChannels) ? (it.blockedChannels as string[]) : [];
          neu[id] = {
            email: !gesperrt.includes('email') && typeof it.email === 'string' ? it.email : null,
            phone: !gesperrt.includes('sms') && typeof it.phone === 'string' ? it.phone : null,
          };
        }
        setKontakte(neu);
      })
      .catch(() => undefined);
    return () => {
      aktiv = false;
    };
  }, [open, event, standortId, participants]);

  const meetingUrl = safeUrl(course?.online_meeting_url) || '';

  const displayAddress = useMemo(() => {
    // prefer Standortadresse from unit API
    if (event?.standortAdresse) return event.standortAdresse;
    // fallback to structured room address if available
    if (!room) return null;
    const parts = [
      [room.address_street, room.address_house_number].filter(Boolean).join(' ').trim(),
      [room.address_postal_code, room.address_city].filter(Boolean).join(' ').trim(),
    ].filter(p => p !== '');
    return parts.length > 0 ? parts.join(', ') : (room.address ?? null);
  }, [event?.standortAdresse, room]);

  const fach = event?.fachName || '';
  const anzahl = participants.length;
  const aktiv = course ? course.is_active && String(course.status || 'active') !== 'inactive' : true;
  const termin = event ? `${wochentagVon(event)}${wochentagVon(event) ? ', ' : ''}${kurzeZeit(event.startTime)}–${kurzeZeit(event.endTime)} Uhr` : '';

  // Nur bei Abweichung ein Hinweis; ist der Kurs wie vorgesehen belegt, steht nichts da.
  const belegung = useMemo(() => {
    const max = Number(form?.max_schueler || 0);
    if (!max || !course) return null;
    if (anzahl > max) return { text: `Überbelegt: ${anzahl} von ${max} Plätzen`, warn: true };
    if (anzahl < max) return { text: `Unterbelegt: ${anzahl} von ${max} Plätzen`, warn: false };
    return null;
  }, [form, course, anzahl]);

  const titel = event
    ? [course ? `Kurs-ID ${course.id}` : event.name || 'Kurs', fach, `${anzahl} Teilnehmer`].filter(Boolean).join(' · ')
    : 'Kurs';

  const notizen = useMemo(
    () => ereignisse.filter((e) => e.type === 'note').sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))),
    [ereignisse]
  );
  const verlauf = useMemo(
    () => ereignisse.filter((e) => e.type !== 'note').sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt))),
    [ereignisse]
  );

  const notizSpeichern = async () => {
    const text = notizNeu.trim();
    if (!text || !event?.courseId) return;
    setSavingNote(true);
    try {
      await api.post('/api/history', { entityType: 'course', entityId: event.courseId, type: 'note', description: text });
      setNotizNeu('');
      await ladeEreignisse(event.courseId);
      toast.success('Notiz gespeichert');
    } catch (e: any) {
      toast.error(e?.message || 'Notiz konnte nicht gespeichert werden');
    } finally {
      setSavingNote(false);
    }
  };

  const terminNotizSpeichern = async () => {
    if (!event?.unitId) return;
    setSavingNote(true);
    try {
      await api.put(`/api/unterrichtseinheiten/${event.unitId}`, { notizen: terminNotiz });
      setTerminNotizEdit(false);
      toast.success('Notiz gespeichert');
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSavingNote(false);
    }
  };

  const meetingSetzen = async (url: string | null) => {
    if (!event?.courseId) {
      toast.error('Online-Link kann nur an einem Kurs gespeichert werden');
      return;
    }
    const candidate = url === null ? null : safeUrl(url);
    if (url !== null && !candidate) {
      toast.error('Bitte eine gültige https:// URL eingeben');
      return;
    }
    setSavingMeeting(true);
    try {
      const updated = await kurseApi.update(event.courseId, { online_meeting_url: candidate } as Partial<KursItem>);
      setCourse(updated);
      setMeetingDraft(candidate || '');
      setMeetingEdit(false);
      toast.success(candidate ? 'Online-Link gespeichert' : 'Online-Link gelöscht');
    } catch (e: any) {
      toast.error(e?.message || 'Online-Link konnte nicht gespeichert werden');
    } finally {
      setSavingMeeting(false);
    }
  };

  const copyMeeting = async () => {
    if (!meetingUrl) return;
    try {
      await navigator.clipboard.writeText(meetingUrl);
      toast.success('Link kopiert');
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const defaultUrl = safeUrl(onlineCfg?.defaultMeetingUrl) || '';

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title={titel}
      description={termin || undefined}
      isLoading={loading}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {onAddTrial && event ? (
              <Button type="button" variant="secondary" onClick={() => onAddTrial(event)} disabled={loading}>
                <Plus className="h-4 w-4 mr-2" />
                Probestunde hinzufügen
              </Button>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" disabled={loading} aria-label="Weitere Aktionen">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => event && onRescheduleCourse?.(event)} disabled={!event}>
                  Kurs verschieben
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => event && onCancelCourse?.(event)} disabled={!event}>
                  Termin absagen
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => event && onContactParticipants?.(event)} disabled={!event}>
                  Alle Teilnehmer kontaktieren
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button type="button" onClick={() => onOpenChange(false)} disabled={loading}>
              Schließen
            </Button>
          </div>
        </div>
      }
    >
      {event ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className={aktiv ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-muted bg-muted text-muted-foreground'}
            >
              {aktiv ? 'Aktiv' : 'Inaktiv'}
            </Badge>
            {belegung ? (
              <Badge
                variant="outline"
                className={belegung.warn ? 'border-red-300 bg-red-50 text-red-800' : 'border-amber-300 bg-amber-50 text-amber-800'}
              >
                {belegung.text}
              </Badge>
            ) : null}
          </div>

          <Tabs defaultValue="uebersicht">
            <TabsList>
              <TabsTrigger value="uebersicht">Übersicht</TabsTrigger>
              <TabsTrigger value="ereignisse">Kursereignisse</TabsTrigger>
            </TabsList>

            <TabsContent value="uebersicht">
              <WizardCard>
                <WizardSection title="Teilnehmer">
                  {participants.length > 0 ? (
                    <ul className="divide-y">
                      {participants.map((p) => (
                        <li key={p.subprofileId} className="flex items-center justify-between gap-3 py-1.5">
                          <button
                            type="button"
                            className="text-left text-sm font-medium hover:underline"
                            onClick={() => onJumpToPerson?.(p)}
                          >
                            {p.name}
                          </button>
                          <KontaktKnoepfe
                            name={p.name}
                            kontakt={kontakte[`p:${p.subprofileId}`]}
                            onNachricht={
                              onContactPerson
                                ? (kanal) => onContactPerson({ wpUserId: p.wpUserId, subIndex: p.subIndex, name: p.name }, kanal)
                                : undefined
                            }
                          />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-sm text-muted-foreground">{event.teilnehmer || 'Keine Teilnehmer'}</div>
                  )}
                </WizardSection>

                <WizardSection title="Lehrkraft">
                  {event.lehrerName ? (
                    <div className="flex items-center justify-between gap-3">
                      {event.lehrerId && onJumpToTeacher ? (
                        <button
                          type="button"
                          className="text-left text-sm font-medium hover:underline"
                          onClick={() => onJumpToTeacher(event.lehrerId as number)}
                        >
                          {event.lehrerName}
                        </button>
                      ) : (
                        <span className="text-sm font-medium">{event.lehrerName}</span>
                      )}
                      {event.lehrerId ? (
                        <KontaktKnoepfe
                          name={event.lehrerName}
                          kontakt={kontakte.lehrkraft}
                          onNachricht={
                            onContactPerson
                              ? (kanal) =>
                                  onContactPerson({ wpUserId: event.lehrerId as number, subIndex: null, name: event.lehrerName || 'Lehrkraft' }, kanal)
                              : undefined
                          }
                        />
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">—</div>
                  )}
                </WizardSection>

                <WizardSection title="Kurs-Details">
                  <WizardKVGrid>
                    <WizardKVItem label={getSubjectLabels().singular}>{fach || '—'}</WizardKVItem>
                    <WizardKVItem label="Unterrichtsform">{form?.name || '—'}</WizardKVItem>
                    <WizardKVItem label="Termin">{termin || '—'}</WizardKVItem>
                    <WizardKVItem label="Raum">
                      <div className="space-y-0.5">
                        {room ? (
                          <button
                            type="button"
                            className="flex items-center gap-2 text-left hover:underline"
                            onClick={() => setRoomDetailsOpen(true)}
                          >
                            <Home className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{room.name || event.raumName || '—'}</span>
                          </button>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Home className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{event.raumName || '—'}</span>
                          </div>
                        )}
                        {displayAddress ? (
                          <div className="flex items-start gap-2 text-xs text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5 mt-0.5" />
                            <span>{displayAddress}</span>
                          </div>
                        ) : null}
                      </div>
                    </WizardKVItem>
                  </WizardKVGrid>
                </WizardSection>

                <WizardSection title="Online-Unterricht">
                  {meetingUrl && !meetingEdit ? (
                    <div className="space-y-2">
                      <div className="break-all text-sm">{meetingUrl}</div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" onClick={() => window.open(meetingUrl, '_blank', 'noopener,noreferrer')}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Beitreten
                        </Button>
                        <Button type="button" variant="outline" onClick={copyMeeting}>
                          <LinkIcon className="h-4 w-4 mr-2" />
                          Kopieren
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setMeetingEdit(true)} disabled={savingMeeting}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Link ändern
                        </Button>
                        <Button type="button" variant="outline" onClick={() => meetingSetzen(null)} disabled={savingMeeting}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Link löschen
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">Der Link steht auch im Kunden- und im Lehrerbereich.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        placeholder="https://…"
                        aria-label="Meeting-Link"
                        value={meetingDraft}
                        onChange={(e) => setMeetingDraft(e.target.value)}
                        disabled={loading || savingMeeting || !event.courseId}
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          onClick={() => meetingSetzen(meetingDraft)}
                          disabled={!event.courseId || loading || savingMeeting || !meetingDraft.trim()}
                        >
                          {meetingUrl ? 'Speichern' : 'Link hinzufügen'}
                        </Button>
                        {defaultUrl ? (
                          <Button type="button" variant="secondary" onClick={() => meetingSetzen(defaultUrl)} disabled={!event.courseId || savingMeeting}>
                            Standard-Link verwenden
                          </Button>
                        ) : null}
                        {meetingEdit ? (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setMeetingEdit(false);
                              setMeetingDraft(meetingUrl);
                            }}
                          >
                            Abbrechen
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  )}
                </WizardSection>

                <WizardSection title="Notizen">
                  <div className="space-y-3">
                    {notizen.length > 0 ? (
                      <ul className="space-y-2">
                        {notizen.map((n) => (
                          <li key={n.id} className="rounded-md border bg-muted/30 p-2 text-sm">
                            <div className="whitespace-pre-wrap">{n.description}</div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              {[n.createdAt ? formatDateTime(n.createdAt) : '', n.actorName || ''].filter(Boolean).join(' · ')}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <Textarea
                      placeholder="Interne Notiz zum Kurs…"
                      aria-label="Neue Notiz"
                      value={notizNeu}
                      onChange={(e) => setNotizNeu(e.target.value)}
                      disabled={!event.courseId || savingNote}
                    />
                    <Button type="button" variant="outline" onClick={notizSpeichern} disabled={!event.courseId || savingNote || !notizNeu.trim()}>
                      Notiz hinzufügen
                    </Button>
                    {event.unitId ? (
                      <div className="space-y-2 border-t pt-3">
                        <div className="text-xs font-medium text-muted-foreground">
                          Notiz zu diesem Termin{event.startDate ? ` (${event.startDate.split('-').reverse().join('.')})` : ''}
                        </div>
                        {terminNotizEdit ? (
                          <>
                            <Textarea
                              aria-label="Notiz zu diesem Termin"
                              value={terminNotiz}
                              onChange={(e) => setTerminNotiz(e.target.value)}
                              disabled={savingNote}
                            />
                            <div className="flex gap-2">
                              <Button type="button" onClick={terminNotizSpeichern} disabled={savingNote}>Speichern</Button>
                              <Button type="button" variant="outline" onClick={() => setTerminNotizEdit(false)} disabled={savingNote}>Abbrechen</Button>
                            </div>
                          </>
                        ) : (
                          <div className="flex items-start justify-between gap-2">
                            <div className="whitespace-pre-wrap text-sm">{terminNotiz.trim() || '—'}</div>
                            <Button type="button" variant="ghost" size="sm" onClick={() => setTerminNotizEdit(true)}>
                              <Pencil className="h-4 w-4 mr-1" />
                              Bearbeiten
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </WizardSection>
              </WizardCard>
            </TabsContent>

            <TabsContent value="ereignisse">
              <WizardCard>
                <WizardSection title="Kursereignisse">
                  {verlauf.length > 0 ? (
                    <ol className="space-y-2">
                      {verlauf.map((e) => (
                        <li key={e.id} className="border-l-2 border-muted pl-3 text-sm">
                          <div>{e.description || e.type}</div>
                          <div className="text-xs text-muted-foreground">
                            {[e.createdAt ? formatDateTime(e.createdAt) : '', e.actorName || ''].filter(Boolean).join(' · ')}
                          </div>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className="text-sm text-muted-foreground">Noch keine Ereignisse zu diesem Kurs.</div>
                  )}
                </WizardSection>
              </WizardCard>
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">Kein Kurs ausgewählt.</div>
      )}

      {room ? (
        <WizardShell
          open={roomDetailsOpen}
          onOpenChange={setRoomDetailsOpen}
          title={`Raum: ${room.name}`}
          description={displayAddress || undefined}
          footer={
            <div className="flex w-full justify-end">
              <Button type="button" variant="outline" onClick={() => setRoomDetailsOpen(false)}>
                Schließen
              </Button>
            </div>
          }
        >
          <WizardCard>
            <WizardSection title="Raum-Details">
              <WizardKVGrid>
                <WizardKVItem label="Name">{room.name}</WizardKVItem>
                <WizardKVItem label="Adresse">{displayAddress || room.address || '—'}</WizardKVItem>
                <WizardKVItem label="Wegbeschreibung">{room.directions || '—'}</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>

            {room.image_url ? (
              <WizardSection title="Foto">
                <div className="overflow-hidden rounded-lg border bg-background">
                  <img
                    src={room.image_url}
                    alt={`Raum ${room.name}`}
                    className="h-[260px] w-full object-cover"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                </div>
              </WizardSection>
            ) : null}

            {displayAddress ? (
              <WizardSection title="Karte">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress)}`, '_blank', 'noopener,noreferrer')}
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  In Google Maps öffnen
                </Button>
              </WizardSection>
            ) : null}
          </WizardCard>
        </WizardShell>
      ) : null}
    </WizardShell>
  );
}
